import { DownOutlined } from '@ant-design/icons'
import {
  Button,
  Dropdown,
  GradientPageHeader,
  Icon,
  IconType
} from '@lmc/ui-kit'
import Search from '@lmc/ui-kit/src/dataEntry/Input/components/Search/Search'
import { Space } from 'antd'
import { debounce } from 'lodash'
import type React from 'react'
import { type Dispatch, type SetStateAction, useMemo } from 'react'
import styled from 'styled-components'
import { commonPatterns } from '../../../../../../src/utils/enhancedTestIds'
import type { AddTeamMemberProps } from './AddTeamMemberModal'

type TeamListHeaderGroupsProps = {
  openModal: (key?: AddTeamMemberProps['type']) => void
  onSearch: Dispatch<SetStateAction<string>>
  title?: string
  searchPlaceholder?: string
  buttonTitle?: string
  buttonDisabled?: boolean
  testId?: string
}

const StyledContainer = styled.div`
  max-width: 100%;
  margin-top: 24px;
  margin-bottom: 0px;
  margin-left: 24px;
  margin-right: 24px;
`

const StyledChild = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    margin-right: auto;
  }
`

const StyledSearch = styled(Search)`
  margin-right: 10px;
  width: 200px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-bottom: 10px;
  }
`

const StyledMail = styled(Icon)``

const StyledSpace = styled(Space)`
  display: flex;
  align-items: center;

  & > * {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const TeamListHeaderGroups: React.FC<TeamListHeaderGroupsProps> = ({
  openModal,
  onSearch,
  title,
  searchPlaceholder,
  buttonTitle,
  buttonDisabled = false,
  testId
}) => {
  const dropdownOptions = useMemo(
    () => ({
      onClick: ({ key }: { key?: string }) =>
        openModal(key as AddTeamMemberProps['type']),
      items: [
        {
          label: 'Team member',
          key: 'addTeamMember',
          icon: <StyledMail customIcon={IconType.Mail} />
        },
        {
          label: 'Group Dashboard User',
          key: 'addGroupDashboardUser',
          icon: <StyledMail customIcon={IconType.Mail} />
        }
      ]
    }),
    [openModal]
  )

  const debouncedSearch = debounce((value, onSearch) => {
    onSearch(value)
  }, 300)

  return (
    <StyledContainer data-testid={testId}>
      <GradientPageHeader
        title={title as string}
        gradient
        testId={testId ? `${testId}-header` : undefined}
      >
        <StyledChild data-testid={testId ? `${testId}-content` : undefined}>
          <StyledSearch
            placeholder={searchPlaceholder}
            onChange={(event) => debouncedSearch(event.target.value, onSearch)}
            data-testid={
              testId ? commonPatterns.input(testId, 'search') : undefined
            }
          />
          <Dropdown
            arrow
            trigger={['click']}
            data-cy="btn-invite-team-member"
            disabled={buttonDisabled}
            menu={dropdownOptions}
            placement="bottomRight"
            data-testid={testId ? `${testId}-dropdown` : undefined}
          >
            <Button
              variant="primary"
              data-testid={
                testId ? commonPatterns.button(testId, 'invite') : undefined
              }
            >
              <StyledSpace>
                <StyledMail customIcon={IconType.Profile} /> {buttonTitle}{' '}
                <DownOutlined />
              </StyledSpace>
            </Button>
          </Dropdown>
        </StyledChild>
      </GradientPageHeader>
    </StyledContainer>
  )
}

export default TeamListHeaderGroups
